import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import AttachmentsSection from '../../components/AttachmentsSection';

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700"
};

const cap = (v) => {
  return v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : "-";
};

const fmtDate = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const s = String(v);
  if (s.length === 10) return s;
  const d = new Date(s);
  return isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString();
};
const fmtAmount = (v) => {
  const n = Number(v || 0);
  if (isNaN(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const docNo = (d) => {
  return d.number || d.invoice_number || d.quotation_number || d.order_number || d.receipt_number || d.credit_note_number || d.title || d.name || "-";
};

const docAmount = (d) => {
  return d.total || d.amount || d.value || 0;
};

const docDate = (d) => {
  return d.created_at || d.date || d.order_date || d.expected_delivery || "";
};

const byClient = (rows, id) => {
  return (rows || []).filter((r) => {
    return r.client_id === id || r.clientId === id;
  });
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState({ leads: [], deals: [], quotations: [], orders: [], invoices: [], payments: [], creditNotes: [] });
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ type: "note", text: "", date: "" });
  const [saving, setSaving] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        api.get("/clients"),
        api.get("/leads"),
        api.get("/deals"),
        api.get("/quotations"),
        api.get("/sales-orders"),
        api.get("/invoices"),
        api.get("/payment-receipts"),
        api.get("/credit-notes"),
        api.get("/client-activities"),
      api.get("/integrations/maya/activities", { params: { client_id: id } })
      ];
      const results = await Promise.all(calls.map((pr) => pr.catch(() => { return { data: [] }; })));
      const allClients = results[0].data || [];
      const found = allClients.find((c) => String(c.id) === String(id));
      setClient(found || null);
      const clientEmail = found ? found.email : "";
      const leadRows = (results[1].data || []).filter((r) => {
        return r.client_id === id || r.clientId === id || (clientEmail !== "" && r.email === clientEmail);
      });
      setDocs({
        leads: leadRows,
        deals: byClient(results[2].data, id),
        quotations: byClient(results[3].data, id),
        orders: byClient(results[4].data, id),
        invoices: byClient(results[5].data, id),
        payments: byClient(results[6].data, id),
        creditNotes: byClient(results[7].data, id)
      });
    try {
      const contactsRes = await api.get("/contacts");
      setContacts(byClient(contactsRes.data, id));
    } catch (e) {
      setContacts([]);
    }
      
const mayaRows = Array.isArray(results[9].data) ? results[9].data : [];
const mergedActivities = byClient(results[8].data, id);
const seenActivityIds = new Set(mergedActivities.map((a) => String(a.id)));
mayaRows.forEach((a) => {
  if (seenActivityIds.has(String(a.id)) === false) {
    seenActivityIds.add(String(a.id));
    mergedActivities.push(a);
  }
});
setActivities(mergedActivities);
    } catch (error) {
      toast.error("Failed to load client details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);
  const timeline = (() => {
    const entries = [];
    activities.forEach((a) => {
      entries.push({
        key: "activity-" + String(a.id),
        date: a.date || a.created_at || "",
        title: cap(a.type),
        text: a.text,
        user: a.user || "",
        source: "Activity"
      });
    });
    const pushDocs = (rows, label) => {
      rows.forEach((d) => {
        entries.push({
          key: label + "-" + String(d.id),
          date: docDate(d),
          title: label + " created",
          text: docNo(d) + " - Status: " + cap(d.status) + " - Amount: " + fmtAmount(docAmount(d)),
          user: "",
          source: label
        });
      });
    };
    pushDocs(docs.leads, "Lead");
    pushDocs(docs.deals, "Deal");
    pushDocs(docs.quotations, "Quotation");
    pushDocs(docs.orders, "Sales order");
    pushDocs(docs.invoices, "Invoice");
    pushDocs(docs.creditNotes, "Credit note");
    docs.payments.forEach((d) => {
      entries.push({
        key: "payment-" + String(d.id),
        date: docDate(d),
        title: "Payment received",
        text: docNo(d) + " - Amount: " + fmtAmount(docAmount(d)),
        user: "",
        source: "Payment"
      });
    });
    entries.sort((a, b) => {
      const ta = new Date(a.date).getTime();
      const tb = new Date(b.date).getTime();
      const na = isNaN(ta) ? 0 : ta;
      const nb = isNaN(tb) ? 0 : tb;
      return nb - na;
    });
    return entries;
  })();
  const addActivity = async () => {
    if (form.text.trim() === "") {
      toast.error("Please enter a note");
      return;
    }
    setSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        client_id: id,
        type: form.type,
        text: form.text.trim(),
        date: form.date === "" ? today : form.date,
        user: (user && (user.name || user.email)) || ""
      };
      const res = await api.post("/client-activities", payload);
      if (res.data) {
        setActivities((prev) => { return [res.data].concat(prev); });
      }
      setForm({ type: "note", text: "", date: "" });
      toast.success("Activity added");
    } catch (error) {
      toast.error("Failed to add activity");
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return (<div className="p-6">Loading client details...</div>);
  }

  if (client === null) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={() => navigate("/crm/clients")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Clients
        </Button>
        <p className="mt-4 text-slate-600">Client not found.</p>
      </div>
    );
  }

  const priority = ["high", "medium", "low"].indexOf(client.priority) === -1 ? "medium" : client.priority;

  const renderDocSection = (title, rows) => {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">No records yet.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((d) => (
                <div key={d.id} className="flex items-center justify-between border-b pb-2">
                  <div>
                    <p className="font-medium text-sm">{docNo(d)}</p>
                    <p className="text-xs text-slate-500">{d.title || d.description || fmtDate(docDate(d))}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{fmtAmount(docAmount(d))}</span>
                    <Badge>{cap(d.status)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate("/crm/clients")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{client.name}</h1>
          <p className="text-sm text-slate-500">{client.company || ""}</p>
        </div>
        <Badge>{cap(client.status)}</Badge>
        <Badge className={PRIORITY_COLORS[priority]}>{cap(priority)}</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contact details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <p><span className="text-slate-500">Email: </span>{client.email || "-"}</p>
            <p><span className="text-slate-500">Phone: </span>{client.phone || "-"}</p>
            <p><span className="text-slate-500">Company: </span>{client.company || "-"}</p>
            <p><span className="text-slate-500">Website: </span>{client.website || "-"}</p>
            <p><span className="text-slate-500">Industry: </span>{client.industry || "-"}</p>
            <p><span className="text-slate-500">Source: </span>{client.source || "-"}</p>
            <p><span className="text-slate-500">Contact person: </span>{client.contact_person || "-"}</p>
          <p><span className="text-slate-500">Contact title: </span>{client.contact_title || "-"}</p>
            <p><span className="text-slate-500">Address: </span>{client.address || "-"}</p>
          <p><span className="text-slate-500">Tax / registration number: </span>{client.tax_id || "-"}</p>
          <p><span className="text-slate-500">Payment terms: </span>{client.payment_terms || "-"}</p>
          </div>
          {client.notes ? (<p className="mt-4 text-sm"><span className="text-slate-500">Notes: </span>{client.notes}</p>) : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Contacts ({contacts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <p className="text-sm text-slate-500">No contacts yet. Add people who work at this client from CRM &gt; Contacts.</p>
          ) : (
            <div className="space-y-2">
              {contacts.map((c) => (
                <div key={c.id} className="text-sm">
                  <p className="font-medium">{c.name}</p>
                  <p className="text-slate-500">{[c.position, c.email, c.phone].filter(Boolean).join(" - ")}</p>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" className="mt-4" onClick={() => navigate("/crm/contacts")}>Manage contacts</Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderDocSection("Leads", docs.leads)}
        {renderDocSection("Deals", docs.deals)}
        {renderDocSection("Quotations", docs.quotations)}
        {renderDocSection("Sales orders", docs.orders)}
        {renderDocSection("Invoices", docs.invoices)}
        {renderDocSection("Payments", docs.payments)}
        {renderDocSection("Credit notes", docs.creditNotes)}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Activity timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
            <Select value={form.type} onValueChange={(val) => setForm({ ...form, type: val })}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="follow-up">Follow-up</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Button onClick={addActivity} disabled={saving}>
              <Plus className="h-4 w-4 mr-2" /> Add activity
            </Button>
          </div>
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Write a note, call summary or follow-up..." rows={3} />
          <div className="mt-6 space-y-4">
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500">No activity yet.</p>
            ) : (
              timeline.map((t) => (
                <div key={t.key} className="border-l-2 border-slate-200 pl-4">
                  <p className="text-xs text-slate-500">{fmtDate(t.date)} - {t.source}</p>
                  <p className="font-medium text-sm">{t.title}</p>
                  <p className="text-sm text-slate-600">{t.text}</p>
                  {t.user ? (<p className="text-xs text-slate-400">{t.user}</p>) : null}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    <AttachmentsSection entityType="client" entityId={client?.id} />
    </div>
  );
};

export default ClientDetail;
